/**
 * Device info for contact submissions (bug reports / feature requests).
 *
 * Collected from what's already bundled — react-native's `Platform` and
 * `expo-constants` — so no native device module (and thus no EAS rebuild) is
 * needed. Every field is best-effort: on web `osVersion`/`deviceModel` are
 * usually blank and the user-agent carries the useful detail; on native the
 * user-agent is omitted.
 */

import { Platform } from 'react-native'
import Constants from 'expo-constants'
import { BUILD_LABEL, formatAppVersion } from './appVersion'
import type { ContactDeviceInfo } from '@warcarp/api-types'

/** Trim to a sane length and drop blanks so we never send empty/huge values. */
function clean(v: unknown, max = 200): string | undefined {
  if (v == null) return undefined
  const s = String(v).trim()
  if (!s) return undefined
  return s.length > max ? s.slice(0, max) : s
}

/**
 * Pure assembly of the device-info payload from raw inputs — no globals, so it
 * is unit-testable. `collectDeviceInfo` supplies the real platform values.
 */
export function normalizeDeviceInfo(raw: {
  platform?: unknown
  osVersion?: unknown
  appVersion?: unknown
  deviceModel?: unknown
  userAgent?: unknown
}): ContactDeviceInfo {
  const info: ContactDeviceInfo = {}
  const platform = clean(raw.platform)
  const osVersion = clean(raw.osVersion)
  const appVersion = clean(raw.appVersion)
  const deviceModel = clean(raw.deviceModel)
  const userAgent = clean(raw.userAgent, 500)
  if (platform) info.platform = platform
  if (osVersion) info.osVersion = osVersion
  if (appVersion) info.appVersion = appVersion
  if (deviceModel) info.deviceModel = deviceModel
  if (userAgent) info.userAgent = userAgent
  return info
}

/** Read the real client environment into a ContactDeviceInfo. */
export function collectDeviceInfo(): ContactDeviceInfo {
  const isWeb = Platform.OS === 'web'
  return normalizeDeviceInfo({
    platform: Platform.OS,
    osVersion: Platform.Version,
    appVersion: formatAppVersion(Constants.expoConfig?.version, BUILD_LABEL),
    deviceModel: Constants.deviceName,
    userAgent: isWeb && typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  })
}
